import { useAuth } from '@/composables/useAuth'
import { useErrorReporter } from '@/composables/useErrorReporter'
import type {
	DeviceRebootResponse,
	DeviceRebootResult,
	DeviceStatusResponse
} from '@/types/api-client/device-tools'
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'

export interface DeviceToolState {
	loading: boolean
	progress: number
	finished: boolean
	error: Error | null
	result: DeviceStatusResponse | DeviceStatusResponse[] | DeviceRebootResponse | null
	cooldownSeconds: number
	cooldownInterval: ReturnType<typeof setInterval> | null
}

const deviceStatusTests: Record<string, DeviceToolState> = reactive({})
const deviceReboots: Record<string, DeviceToolState> = reactive({})

/**
 * Configuration interface for device tools API domain selection
 */
export interface UseDeviceToolsConfig {
	apiDomain: 'internal' | 'customer'
	/** Account ID to use for API calls (required for internal portal, optional for customer portal) */
	accountId?: number | string
}

/**
 * Device tools composable for status checks and reboots
 * Supports configuration-based API selection for portal-agnostic usage
 *
 * @param config - Configuration object with apiDomain property
 * @returns Device tools methods and reactive state
 */
export function useDeviceTools(config: UseDeviceToolsConfig) {
	const { bugsnagReport } = useErrorReporter()
	const { accountId: authAccountId, fullName, email, emailId } = useAuth()
	const { t } = useI18n()
	const { $api } = useNuxtApp()

	// Select API client based on configuration
	const deviceToolsApi = config.apiDomain === 'internal'
		? $api.internalDeviceTools
		: $api.deviceTools

	// Use config accountId if provided (internal portal), otherwise use auth accountId (customer portal)
	const getEffectiveAccountId = (): number => {
		if (config.accountId !== undefined) {
			return Number(config.accountId)
		}
		return Number(authAccountId.value)
	}

	const getDeviceKey = (macAddress: string) => macAddress?.replace(/:/g, '')?.toLowerCase()

	const initializeState = (deviceKey: string, stateStore: Record<string, DeviceToolState>) => {
		if (!stateStore[deviceKey]) {
			stateStore[deviceKey] = {
				loading: false,
				progress: 0,
				finished: false,
				error: null,
				result: null,
				cooldownSeconds: 0,
				cooldownInterval: null
			}
		}
	}

	const startCooldown = (deviceKey: string, stateStore: Record<string, DeviceToolState>) => {
		const state = stateStore[deviceKey]

		// Clear any existing interval
		if (state.cooldownInterval) {
			clearInterval(state.cooldownInterval)
		}

		state.cooldownSeconds = 10

		state.cooldownInterval = setInterval(() => {
			if (state.cooldownSeconds > 0) {
				state.cooldownSeconds--
			} else {
				if (state.cooldownInterval) {
					clearInterval(state.cooldownInterval)
					state.cooldownInterval = null
				}
			}
		}, 1000)
	}

	const simulateProgress = async (
		deviceKey: string,
		stateStore: Record<string, DeviceToolState>,
		minimumDuration: number = 2000
	) => {
		const startTime = Date.now()
		const updateInterval = 50

		return new Promise<void>(resolve => {
			const interval = setInterval(() => {
				const elapsed = Date.now() - startTime
				if (elapsed >= minimumDuration || stateStore[deviceKey].progress >= 100) {
					stateStore[deviceKey].progress = 100
					clearInterval(interval)
					resolve()
				} else {
					stateStore[deviceKey].progress = Math.min(
						100,
						(elapsed / minimumDuration) * 100
					)
				}
			}, updateInterval)
		})
	}

	const checkDeviceStatus = async (
		macAddress: string,
		serialNumber?: string,
		deviceNms?: string
	) => {
		const deviceKey = getDeviceKey(macAddress)
		initializeState(deviceKey, deviceStatusTests)

		const state = deviceStatusTests[deviceKey]

		// Check if cooldown is active
		if (state.cooldownSeconds > 0) {
			return
		}

		state.loading = true
		state.progress = 0
		state.finished = false
		state.error = null
		state.result = null

		const progressPromise = simulateProgress(deviceKey, deviceStatusTests)

		try {
			const response = await deviceToolsApi.checkDeviceStatus(
				getEffectiveAccountId(),
				{
					macAddress,
					serialNumber,
					deviceNms
				},
				{ autoError: false }
			)

			await progressPromise

			if (response) {
				state.result = response
				state.finished = true
				startCooldown(deviceKey, deviceStatusTests)
			} else {
				throw new Error('Device status check failed')
			}
		} catch (error) {
			state.error = error instanceof Error ? error : new Error(String(error))
			state.finished = true
			startCooldown(deviceKey, deviceStatusTests)

			bugsnagReport({
				error: state.error,
				showToast: true,
				toast: {
					notificationType: 'error',
					title: t('device-tools.toasts.status-check-failed')
				},
				metaData: {
					macAddress,
					serialNumber,
					deviceNms
				},
				user: {
					id: getEffectiveAccountId().toString(),
					emailId: emailId.value,
					email: email.value,
					name: fullName.value
				}
			})
		} finally {
			state.loading = false
		}
	}

	const rebootDevice = async (
		macAddress: string,
		serialNumber?: string,
		deviceNms?: string
	) => {
		const deviceKey = getDeviceKey(macAddress)
		initializeState(deviceKey, deviceReboots)

		const state = deviceReboots[deviceKey]

		// Check if cooldown is active
		if (state.cooldownSeconds > 0) {
			return
		}

		state.loading = true
		state.progress = 0
		state.finished = false
		state.error = null
		state.result = null

		const progressPromise = simulateProgress(deviceKey, deviceReboots, 3000)

		try {
			const response = await deviceToolsApi.rebootDevice(
				getEffectiveAccountId(),
				{
					macAddress,
					serialNumber,
					deviceNms
				},
				{ autoError: false }
			)

			await progressPromise

			if (response && response.success) {
				state.result = response
				state.finished = true
				startCooldown(deviceKey, deviceReboots)
			} else {
				throw new Error('Device reboot failed')
			}
		} catch (error) {
			state.error = error instanceof Error ? error : new Error(String(error))
			state.finished = true
			startCooldown(deviceKey, deviceReboots)

			bugsnagReport({
				error: state.error,
				showToast: true,
				toast: {
					notificationType: 'error',
					title: t('device-tools.toasts.reboot-failed')
				},
				metaData: {
					macAddress,
					serialNumber,
					deviceNms
				},
				user: {
					id: getEffectiveAccountId().toString(),
					emailId: emailId.value,
					email: email.value,
					name: fullName.value
				}
			})
		} finally {
			state.loading = false
		}
	}

	const resetDeviceStatus = (macAddress: string) => {
		const deviceKey = getDeviceKey(macAddress)
		if (deviceStatusTests[deviceKey]) {
			// Clear any existing cooldown interval
			if (deviceStatusTests[deviceKey].cooldownInterval) {
				clearInterval(deviceStatusTests[deviceKey].cooldownInterval)
			}
			deviceStatusTests[deviceKey] = {
				loading: false,
				progress: 0,
				finished: false,
				error: null,
				result: null,
				cooldownSeconds: 0,
				cooldownInterval: null
			}
		}
	}

	const resetDeviceReboot = (macAddress: string) => {
		const deviceKey = getDeviceKey(macAddress)
		if (deviceReboots[deviceKey]) {
			// Clear any existing cooldown interval
			if (deviceReboots[deviceKey].cooldownInterval) {
				clearInterval(deviceReboots[deviceKey].cooldownInterval)
			}
			deviceReboots[deviceKey] = {
				loading: false,
				progress: 0,
				finished: false,
				error: null,
				result: null,
				cooldownSeconds: 0,
				cooldownInterval: null
			}
		}
	}

	// Mock data injection for dev testing
	const injectMockDeviceStatus = (
		macAddress: string,
		mockResponse: DeviceStatusResponse | DeviceStatusResponse[],
		mockId?: string
	) => {
		const deviceKey = getDeviceKey(macAddress)
		initializeState(deviceKey, deviceStatusTests)

		const state = deviceStatusTests[deviceKey]

		// Handle special states (loading, cooldown)
		if (mockId === 'loading') {
			state.loading = true
			state.progress = 50
			state.finished = false
			state.error = null
			state.result = null
			state.cooldownSeconds = 0
		}
		else if (mockId?.startsWith('cooldown')) {
			state.loading = false
			state.progress = 100
			state.finished = true
			state.error = null
			state.result = mockResponse
			state.cooldownSeconds = 30
		}
		else {
			// Normal result state
			state.loading = false
			state.progress = 100
			state.finished = true
			state.error = null
			state.result = mockResponse
			state.cooldownSeconds = 0
		}

		if (state.cooldownInterval) {
			clearInterval(state.cooldownInterval)
			state.cooldownInterval = null
		}
	}

	const injectMockDeviceReboot = (macAddress: string, mockResult: Partial<DeviceRebootResult>) => {
		const deviceKey = getDeviceKey(macAddress)
		initializeState(deviceKey, deviceReboots)

		const state = deviceReboots[deviceKey]

		// Map DeviceRebootResult to DeviceToolState
		state.loading = mockResult.isLoading ?? false
		state.progress = mockResult.progress ?? 100
		state.finished = mockResult.finished ?? true
		state.error = mockResult.error ?? null
		state.result = mockResult.success ? { success: true } : null
		state.cooldownSeconds = mockResult.cooldownSeconds ?? 0

		if (state.cooldownInterval) {
			clearInterval(state.cooldownInterval)
			state.cooldownInterval = null
		}
	}

	return {
		checkDeviceStatus,
		rebootDevice,
		resetDeviceStatus,
		resetDeviceReboot,
		injectMockDeviceStatus,
		injectMockDeviceReboot,
		deviceStatusTests,
		deviceReboots,
		getDeviceKey
	}
}
